"use strict";
//
// Copyright © 2020, 2021 Anticrm Platform Contributors.
// Copyright © 2026 Intabia Fusion.
//
// Licensed under the Eclipse Public License, Version 2.0 (the "License");
// you may not use this file except in compliance with the License. You may
// obtain a copy of the License at https://www.eclipse.org/legal/epl-2.0
//
// Unless required by applicable law or agreed to in writing, software
// distributed under the License is distributed on an "AS IS" BASIS,
// WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
//
// See the License for the specific language governing permissions and
// limitations under the License.
//
var __importDefault = (this && this.__importDefault) || function (mod) {
    return (mod && mod.__esModule) ? mod : { "default": mod };
};
Object.defineProperty(exports, "__esModule", { value: true });
exports.TxFactory = exports.TxProcessor = exports.DOMAIN_TX = exports.WorkspaceEvent = void 0;
exports.isMixinTx = isMixinTx;
exports.getTxOperations = getTxOperations;
const clone_1 = require("./clone");
const component_1 = __importDefault(require("./component"));
const objvalue_1 = require("./objvalue");
const operator_1 = require("./operator");
const proxy_1 = require("./proxy");
const utils_1 = require("./utils");
/**
 * @public
 */
var WorkspaceEvent;
(function (WorkspaceEvent) {
    WorkspaceEvent[WorkspaceEvent["UpgradeScheduled"] = 0] = "UpgradeScheduled";
    WorkspaceEvent[WorkspaceEvent["IndexingUpdate"] = 1] = "IndexingUpdate";
    WorkspaceEvent[WorkspaceEvent["SecurityChange"] = 2] = "SecurityChange";
    WorkspaceEvent[WorkspaceEvent["MaintenanceNotification"] = 3] = "MaintenanceNotification";
    WorkspaceEvent[WorkspaceEvent["BulkUpdate"] = 4] = "BulkUpdate";
    WorkspaceEvent[WorkspaceEvent["LastTx"] = 5] = "LastTx";
    WorkspaceEvent[WorkspaceEvent["LimitsChanged"] = 6] = "LimitsChanged";
})(WorkspaceEvent || (exports.WorkspaceEvent = WorkspaceEvent = {}));
/**
 * @public
 */
exports.DOMAIN_TX = 'tx';
/**
 * @public
 */
class TxProcessor {
    async tx(...txes) {
        const result = [];
        for (const tx of txes) {
            switch (tx._class) {
                case component_1.default.class.TxCreateDoc:
                    result.push(await this.txCreateDoc(tx));
                    break;
                case component_1.default.class.TxUpdateDoc:
                    result.push(await this.txUpdateDoc(tx));
                    break;
                case component_1.default.class.TxRemoveDoc:
                    result.push(await this.txRemoveDoc(tx));
                    break;
                case component_1.default.class.TxMixin:
                    result.push(await this.txMixin(tx));
                    break;
                case component_1.default.class.TxApplyIf:
                    // Apply if processed on server
                    return await Promise.resolve([]);
            }
        }
        return result;
    }
    static createDoc2Doc(tx, doClone = true) {
        const attached = tx.attachedTo !== undefined
            ? {
                attachedTo: tx.attachedTo,
                attachedToClass: tx.attachedToClass,
                collection: tx.collection
            }
            : {};
        // eslint-disable-next-line @typescript-eslint/consistent-type-assertions
        return {
            ...(doClone ? (0, clone_1.clone)(tx.attributes) : tx.attributes),
            ...attached,
            _id: tx.objectId,
            _class: tx.objectClass,
            space: tx.objectSpace,
            modifiedBy: tx.modifiedBy,
            modifiedOn: tx.modifiedOn,
            createdBy: tx.createdBy ?? tx.modifiedBy,
            createdOn: tx.createdOn ?? tx.modifiedOn
        };
    }
    static updateDoc2Doc(rawDoc, tx) {
        const doc = (0, proxy_1._toDoc)(rawDoc);
        TxProcessor.applyUpdate(doc, tx.operations);
        doc.modifiedBy = tx.modifiedBy;
        doc.modifiedOn = tx.modifiedOn;
        return rawDoc;
    }
    static applyUpdate(doc, ops) {
        for (const key in ops) {
            if (key.startsWith('$')) {
                const operator = (0, operator_1._getOperator)(key);
                operator(doc, ops[key]);
            }
            else {
                (0, objvalue_1.setObjectValue)(key, doc, ops[key]);
            }
        }
    }
    static updateMixin4Doc(rawDoc, tx) {
        const ops = tx.attributes;
        const doc = (0, proxy_1._toDoc)(rawDoc);
        const mixin = doc[tx.mixin] ?? {};
        for (const key in ops) {
            if (key.startsWith('$')) {
                const operator = (0, operator_1._getOperator)(key);
                operator(mixin, ops[key]);
            }
            else {
                (0, objvalue_1.setObjectValue)(key, mixin, ops[key]);
            }
        }
        rawDoc.modifiedBy = tx.modifiedBy;
        rawDoc.modifiedOn = tx.modifiedOn;
        doc[tx.mixin] = mixin;
        return rawDoc;
    }
    static buildDoc2Doc(txes) {
        let doc;
        const deleteTx = txes.find((tx) => tx._class === component_1.default.class.TxRemoveDoc);
        if (deleteTx !== undefined) {
            return null;
        }
        const createTx = txes.find((tx) => tx._class === component_1.default.class.TxCreateDoc);
        if (createTx === undefined) {
            return;
        }
        doc = TxProcessor.createDoc2Doc(createTx);
        for (const tx of txes) {
            if (tx._class === component_1.default.class.TxUpdateDoc) {
                doc = TxProcessor.updateDoc2Doc(doc, tx);
            }
            else if (tx._class === component_1.default.class.TxMixin) {
                const mixinTx = tx;
                doc = TxProcessor.updateMixin4Doc(doc, mixinTx);
            }
        }
        return doc;
    }
    static isExtendsCUD(_class) {
        return (_class === component_1.default.class.TxCreateDoc ||
            _class === component_1.default.class.TxUpdateDoc ||
            _class === component_1.default.class.TxRemoveDoc ||
            _class === component_1.default.class.TxMixin);
    }
    static txHasUpdate(tx, attribute) {
        return TxProcessor.hasUpdate(tx.operations, attribute);
    }
    static hasUpdate(ops, attribute) {
        if (ops[attribute] !== undefined)
            return true;
        for (const op in ops) {
            if (op.startsWith('$')) {
                const opValue = ops[op];
                for (const key in opValue) {
                    if (key === attribute || key.startsWith(attribute + '.')) {
                        return true;
                    }
                }
            }
        }
        return false;
    }
}
exports.TxProcessor = TxProcessor;
/**
 * @public
 */
class TxFactory {
    account;
    isDerived;
    txSpace;
    constructor(account, isDerived = false) {
        this.account = account;
        this.isDerived = isDerived;
        this.txSpace = isDerived ? component_1.default.space.DerivedTx : component_1.default.space.Tx;
    }
    createTxCreateDoc(_class, space, attributes, objectId, modifiedOn, modifiedBy, meta) {
        return {
            _id: (0, utils_1.generateId)(),
            _class: component_1.default.class.TxCreateDoc,
            space: this.txSpace,
            objectId: objectId ?? (0, utils_1.generateId)(),
            objectClass: _class,
            objectSpace: space,
            modifiedOn: modifiedOn ?? Date.now(),
            modifiedBy: modifiedBy ?? this.account,
            createdBy: modifiedBy ?? this.account,
            attributes,
            ...(meta !== undefined ? { meta } : {})
        };
    }
    createTxCollectionCUD(_class, objectId, space, collection, tx, modifiedOn, modifiedBy, meta) {
        return {
            ...tx,
            collection,
            attachedTo: objectId,
            attachedToClass: _class,
            modifiedOn: modifiedOn ?? Date.now(),
            modifiedBy: modifiedBy ?? this.account,
            ...(meta !== undefined ? { meta: { ...(tx.meta ?? {}), ...meta } } : {})
        };
    }
    createTxUpdateDoc(_class, space, objectId, operations, retrieve, modifiedOn, modifiedBy, meta) {
        return {
            _id: (0, utils_1.generateId)(),
            _class: component_1.default.class.TxUpdateDoc,
            space: this.txSpace,
            modifiedBy: modifiedBy ?? this.account,
            modifiedOn: modifiedOn ?? Date.now(),
            objectId,
            objectClass: _class,
            objectSpace: space,
            operations,
            retrieve,
            ...(meta !== undefined ? { meta } : {})
        };
    }
    createTxRemoveDoc(_class, space, objectId, modifiedOn, modifiedBy, meta) {
        return {
            _id: (0, utils_1.generateId)(),
            _class: component_1.default.class.TxRemoveDoc,
            space: this.txSpace,
            modifiedBy: modifiedBy ?? this.account,
            modifiedOn: modifiedOn ?? Date.now(),
            objectId,
            objectClass: _class,
            objectSpace: space,
            ...(meta !== undefined ? { meta } : {})
        };
    }
    createTxMixin(objectId, objectClass, objectSpace, mixin, attributes, modifiedOn, modifiedBy, meta) {
        return {
            _id: (0, utils_1.generateId)(),
            _class: component_1.default.class.TxMixin,
            space: this.txSpace,
            modifiedBy: modifiedBy ?? this.account,
            modifiedOn: modifiedOn ?? Date.now(),
            objectId,
            objectClass,
            objectSpace,
            mixin,
            attributes,
            ...(meta !== undefined ? { meta } : {})
        };
    }
    createTxApplyIf(space, scope, match, notMatch, txes, measureName, notify = true, extraNotify = [], modifiedOn, modifiedBy, meta) {
        return {
            _id: (0, utils_1.generateId)(),
            _class: component_1.default.class.TxApplyIf,
            space: this.txSpace,
            modifiedBy: modifiedBy ?? this.account,
            modifiedOn: modifiedOn ?? Date.now(),
            objectSpace: space,
            scope,
            match,
            notMatch,
            txes,
            measureName,
            notify,
            extraNotify,
            ...(meta !== undefined ? { meta } : {})
        };
    }
}
exports.TxFactory = TxFactory;
function isMixinTx(tx) {
    return tx._class === component_1.default.class.TxMixin;
}
function getTxOperations(tx) {
    if (tx._class === component_1.default.class.TxUpdateDoc) {
        const uTx = tx;
        return uTx.operations;
    }
    else if (tx._class === component_1.default.class.TxMixin) {
        const mTx = tx;
        return mTx.attributes;
    }
    else if (tx._class === component_1.default.class.TxCreateDoc) {
        const cTx = tx;
        return cTx.attributes;
    }
    return [];
}
//# sourceMappingURL=tx.js.map